import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, ChangeEventHandler, KeyboardEventHandler } from "react";
import dayjs from "dayjs";
import isLeapYear from "dayjs/plugin/isLeapYear";
import { RootState, AppDispatch } from "app/store";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { SpinModal, DialogMessage } from "common/elements";
import { updateUser } from "features/user/api";
import { IconSelector, UserType, DisplayMode, ProfileInfo } from "features/iconSelector";
import { ProfileTab, BaseColorKey, COLOR_PALETTE, generateColorVariations } from "./constants";
import {
  ProfileTabBar,
  ProfileBasicTab,
  ProfileFavoritesTab,
  ProfileAppearanceTab,
  ProfileEditModal,
  EditingField,
} from "./components";

export const UserProfile = ({ selected }: { selected: boolean }) => {
  dayjs.extend(isLeapYear);
  const play = usePlaySound();
  const [error, setError] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const bannedWords = useSelector((state: RootState) => state.config.banned.words);
  const user = useSelector((state: RootState) => state.user.active);

  const [icon, setIcon] = useState(user.icon);
  const [name, setName] = useState(user.display_name);
  const [gender, setGender] = useState(user.gender);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  const [favoriteColors, setFavoriteColors] = useState<string[]>(user.favorite_color || []);
  const [bestSubjects, setBestSubjects] = useState<string[]>(user.best_subject || []);
  const [selfIntroduction, setSelfIntroduction] = useState<string>(user.self_introduction || "");

  const [accessory, setAccessory] = useState<string[]>(user.accessory || []);
  const [hairstyle, setHairstyle] = useState<string>(user.hairstyle || "");
  const [bodyType, setBodyType] = useState<string>(user.body_type || "");
  const [faceShape, setFaceShape] = useState<string>(user.face_shape || "");

  const [activeTab, setActiveTab] = useState(ProfileTab.BASIC);

  const [editingField, setEditingField] = useState<EditingField>(null);
  const [tempValues, setTempValues] = useState<string[]>([]);
  const [tempText, setTempText] = useState<string>("");

  const [tempBaseColor, setTempBaseColor] = useState<BaseColorKey | null>(null);
  const [tempVariationColor, setTempVariationColor] = useState<string | null>(null);

  const applyBirthdayState = (birthday: string) => {
    if (!birthday) {
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDay(null);
      return;
    }

    const birthdayStr = birthday.split("-");
    if (birthdayStr[0] !== "0000") {
      const parsedBirthday = dayjs(birthday);
      setSelectedYear(parsedBirthday.year());
      setSelectedMonth(parsedBirthday.month() + 1);
      setSelectedDay(parsedBirthday.date());
    } else {
      setSelectedYear(null);
      if (birthdayStr[1] !== "00") setSelectedMonth(Number(birthdayStr[1]));
      else setSelectedMonth(null);
      if (birthdayStr[2] !== "00") setSelectedDay(Number(birthdayStr[2]));
      else setSelectedDay(null);
    }
  };

  const applyProfileInfo = (profileInfo: ProfileInfo) => {
    if (profileInfo.gender !== undefined) setGender(profileInfo.gender || "");
    if (profileInfo.birthday !== undefined) applyBirthdayState(profileInfo.birthday || "");
    if (profileInfo.favorite_color !== undefined)
      setFavoriteColors(profileInfo.favorite_color || []);
    if (profileInfo.best_subject !== undefined) setBestSubjects(profileInfo.best_subject || []);
    if (profileInfo.accessory !== undefined) setAccessory(profileInfo.accessory || []);
    if (profileInfo.hairstyle !== undefined) setHairstyle(profileInfo.hairstyle || "");
    if (profileInfo.body_type !== undefined) setBodyType(profileInfo.body_type || "");
    if (profileInfo.face_shape !== undefined) setFaceShape(profileInfo.face_shape || "");
    if (profileInfo.self_introduction !== undefined)
      setSelfIntroduction(profileInfo.self_introduction || "");
  };

  useEffect(() => {
    setIcon(user.icon);
    setName(user.display_name);
    setGender(user.gender);
    setFavoriteColors(user.favorite_color || []);
    setBestSubjects(user.best_subject || []);
    setSelfIntroduction(user.self_introduction || "");
    setAccessory(user.accessory || []);
    setHairstyle(user.hairstyle || "");
    setBodyType(user.body_type || "");
    setFaceShape(user.face_shape || "");
    applyBirthdayState(user.birthday);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  const handleChangeName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setName(e.target.value);
  };

  const handleChangeGender = (genderValue: string) => {
    setGender(genderValue);
  };

  const handleTabChange = (tab: ProfileTab) => {
    setActiveTab(tab);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (selectedMonth && selectedDay) {
      const maxDay = dayjs(`${year}-${selectedMonth}-01`).daysInMonth();
      if (selectedDay > maxDay) setSelectedDay(maxDay);
    }
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    if (selectedYear && selectedDay) {
      const maxDay = dayjs(`${selectedYear}-${month}-01`).daysInMonth();
      if (selectedDay > maxDay) setSelectedDay(maxDay);
    }
  };

  const handleDayChange = (day: number) => {
    setSelectedDay(day);
  };

  const openEditModal = (field: EditingField) => {
    play();
    setEditingField(field);
    switch (field) {
      case "color":
        if (favoriteColors.length > 0 && favoriteColors[0].startsWith("#")) {
          const savedColor = favoriteColors[0];

          // 保存された色からベース色を逆算
          let foundBase: BaseColorKey | null = null;
          let matchedVariation: string | null = null;
          for (const palette of COLOR_PALETTE) {
            const variations = generateColorVariations(palette.key);
            const matchIndex = variations.findIndex(
              (v) => v.toUpperCase() === savedColor.toUpperCase(),
            );
            if (matchIndex !== -1) {
              foundBase = palette.key;
              matchedVariation = variations[matchIndex];
              break;
            }
          }
          setTempBaseColor(foundBase);
          setTempVariationColor(matchedVariation);
        } else {
          setTempBaseColor(null);
          setTempVariationColor(null);
        }
        break;
      case "bestSubjects":
        setTempValues([...bestSubjects]);
        break;
      case "accessory":
        setTempValues([...accessory]);
        break;
      case "selfIntroduction":
        setTempText(selfIntroduction);
        break;
    }
  };

  const closeEditModal = () => {
    setEditingField(null);
    setTempValues([]);
    setTempText("");
    setTempBaseColor(null);
    setTempVariationColor(null);
  };

  const handleTempSelect = (value: string) => {
    // アクセサリー編集時の排他制御
    if (editingField === "accessory") {
      if (value === "none") {
        // 「なし」を選択：他をすべて解除して「なし」のみにする
        if (tempValues.includes("none")) {
          setTempValues([]);
        } else {
          setTempValues(["none"]);
        }
        return;
      }
      // 他のアクセサリーを選択：「なし」を解除
      const withoutNone = tempValues.filter((v) => v !== "none");
      if (withoutNone.includes(value)) {
        setTempValues(withoutNone.filter((v) => v !== value));
      } else {
        setTempValues([...withoutNone, value]);
      }
      return;
    }

    // 通常のトグル処理（bestSubjects等）
    if (tempValues.includes(value)) {
      setTempValues(tempValues.filter((v) => v !== value));
    } else {
      setTempValues([...tempValues, value]);
    }
  };

  const handleBaseColorSelect = (baseKey: BaseColorKey) => {
    setTempBaseColor(baseKey);
    setTempVariationColor(null);
  };

  const handleVariationSelect = (hex: string) => {
    setTempVariationColor(hex);
  };

  const handleTempTextChange = (text: string) => {
    setTempText(text);
  };

  const confirmEdit = () => {
    switch (editingField) {
      case "color":
        if (tempVariationColor) {
          setFavoriteColors([tempVariationColor]);
        } else {
          setFavoriteColors([]);
        }
        break;
      case "bestSubjects":
        setBestSubjects([...tempValues]);
        break;
      case "accessory":
        setAccessory([...tempValues]);
        break;
      case "selfIntroduction":
        setSelfIntroduction(tempText);
        break;
    }
    closeEditModal();
  };

  const arraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  };

  const formatCurrentBirthday = (): string => {
    if (selectedYear && selectedMonth && selectedDay) {
      return dayjs(`${selectedYear}-${selectedMonth}-${selectedDay}`).format("YYYY-MM-DD");
    } else if (selectedMonth && selectedDay) {
      return `0000-${String(selectedMonth).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;
    }
    return "0000-00-00";
  };

  const hasProfileChanged = (): boolean => {
    if (icon !== user.icon) return true;
    if (name !== user.display_name) return true;
    if (gender !== user.gender) return true;
    const currentBirthday = formatCurrentBirthday();
    if (currentBirthday !== (user.birthday || "0000-00-00")) return true;
    const originalColor = user.favorite_color?.[0] || null;
    const currentColor = favoriteColors[0] || null;
    if (currentColor !== originalColor) return true;
    if (!arraysEqual(bestSubjects, user.best_subject || [])) return true;
    if (selfIntroduction !== (user.self_introduction || "")) return true;
    if (!arraysEqual(accessory, user.accessory || [])) return true;
    if (hairstyle !== (user.hairstyle || "")) return true;
    if (bodyType !== (user.body_type || "")) return true;
    if (faceShape !== (user.face_shape || "")) return true;
    return false;
  };

  const handleConfirm = () => {
    if (name.length === 0) return;
    setLoading(true);
    btnDelay(() => {
      play();
      if (bannedWords.filter((word) => name.includes(word.word)).length > 0) {
        setError(true);
        setLoading(false);
      } else {
        let birthday = "0000-00-00";
        if (selectedYear && selectedMonth && selectedDay) {
          birthday = dayjs(`${selectedYear}-${selectedMonth}-${selectedDay}`).format("YYYY-MM-DD");
        } else if (selectedMonth && selectedDay) {
          birthday = `0000-${String(selectedMonth).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;
        }

        dispatch(
          updateUser({
            uid: user.uid,
            userInfo: {
              icon,
              name,
              birthday,
              gender,
              favorite_color: favoriteColors.length > 0 ? favoriteColors : undefined,
              best_subject: bestSubjects.length > 0 ? bestSubjects : undefined,
              accessory: accessory.length > 0 ? accessory : undefined,
              hairstyle: hairstyle || undefined,
              body_type: bodyType || undefined,
              face_shape: faceShape || undefined,
              self_introduction: selfIntroduction || null,
            },
          }),
        )
          .unwrap()
          .then(() => {
            toast(
              <div>
                <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
              </div>,
            );
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const handleWarningClose = () => {
    play();
    setError(false);
  };

  const handleOpenIconModal = () => {
    play();
    btnDelay(() => setIconModalOpen(true));
  };

  const handleCloseIconModal = () => {
    setIconModalOpen(false);
  };

  const handleEnterKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.keyCode === 13 && !(name.length === 0 || name.length > 10) && hasProfileChanged()) {
      handleConfirm();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case ProfileTab.BASIC:
        return (
          <ProfileBasicTab
            user={user}
            icon={icon}
            name={name}
            gender={gender}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            selectedDay={selectedDay}
            onNameChange={handleChangeName}
            onGenderChange={handleChangeGender}
            onYearChange={handleYearChange}
            onMonthChange={handleMonthChange}
            onDayChange={handleDayChange}
            onOpenIconModal={handleOpenIconModal}
          />
        );
      case ProfileTab.FAVORITES:
        return (
          <ProfileFavoritesTab
            favoriteColors={favoriteColors}
            bestSubjects={bestSubjects}
            selfIntroduction={selfIntroduction}
            onOpenEditModal={openEditModal}
          />
        );
      case ProfileTab.APPEARANCE:
        return (
          <ProfileAppearanceTab
            accessory={accessory}
            hairstyle={hairstyle}
            bodyType={bodyType}
            faceShape={faceShape}
            onOpenAccessoryModal={() => openEditModal("accessory")}
            onHairstyleChange={setHairstyle}
            onBodyTypeChange={setBodyType}
            onFaceShapeChange={setFaceShape}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading && <SpinModal />}

      <DialogMessage
        open={error}
        title={I18n.t("MSG_USER_PROFILE_NAME_ERROR_TITLE")}
        message={I18n.t("MSG_USER_PROFILE_NAME_ERROR_MESSAGE")}
        close={handleWarningClose}
      />

      <IconSelector
        open={iconModalOpen}
        onClose={handleCloseIconModal}
        currentIcon={icon}
        targetUid={user.uid}
        userType={UserType.CHILD}
        mode={DisplayMode.MODAL}
        onSelect={(iconUrl, profileInfo?: ProfileInfo) => {
          setIcon(iconUrl);
          if (profileInfo) {
            applyProfileInfo(profileInfo);
          }
        }}
      />

      <ProfileEditModal
        editingField={editingField}
        tempValues={tempValues}
        tempText={tempText}
        tempBaseColor={tempBaseColor}
        tempVariationColor={tempVariationColor}
        onTempSelect={handleTempSelect}
        onBaseColorSelect={handleBaseColorSelect}
        onVariationSelect={handleVariationSelect}
        onTempTextChange={handleTempTextChange}
        onClose={closeEditModal}
        onConfirm={confirmEdit}
      />

      <div
        onKeyDown={handleEnterKeyDown}
        style={{ display: !selected && "none" }}
        className="flex-col-el items-center justify-start mt-4 w-full h-full"
      >
        <div className="w-[700px]">
          <ProfileTabBar activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="flex-col-el flex-center flex-1 w-full overflow-hidden">
          {renderTabContent()}
        </div>

        <div className="flex-row-el flex-center alpha my-4">
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={name.length === 0 || name.length > 10 || !hasProfileChanged()}
          >
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">check</span>
              <p className="text-body text-white text-font-size-body1">{I18n.t("MSG_SAVE_BTN")}</p>
            </div>
          </button>
        </div>
      </div>
    </>
  );
};
